<template>
    <aside
        id="sectors-sidebar"
        class="generic-sub-sidebar"
        :class="{ 'sectors-sidebar-active': isOpen, 'show-sub-sidebar-header': isOpen && isShowingSubSidebarHeaders }"
    >
        <div class="gradient-pre-header"></div>
        <div class="header">
            <div class="title d-flex flex-row align-items-center mb-4">
                <icon class="mr-2" name="layer-group" color="var(--secondary)" scale="0.75" />
                <p class="text-16">{{ selectedSector.name }}</p>
            </div>
            <div class="d-flex align-items-center justify-content-between">
                <div class="search-input-container mr-2">
                    <icon class="search-icon" name="search" color="var(--secondary-3)" scale=".5" />
                    <input v-model="searchText" class="search-input" :placeholder="$t('keyword167')" />
                </div>
                <div class="dates-filter-container">
                    <ci-button
                        @click="isDatesFilterOpen = !isDatesFilterOpen"
                        :centerIcon="{ name: 'ci-calendar', color: selectedDatesArray && selectedDatesArray.length > 0 ? 'var(--secondary)' : 'white' }"
                        type="primary-1"
                        :class="{ 'filter-active': selectedDatesArray && selectedDatesArray.length > 0 }"
                    />
                    <div v-show="isDatesFilterOpen">
                        <ci-filter-date-select class="dates-filter" :dates="project.capture_dates" v-model="selectedDatesArray" />
                    </div>
                </div>
            </div>
        </div>
        <div class="content d-flex flex-column">
            <div class="gradient-divider"></div>
            <vue-custom-scrollbar ref="scroll">
                <ul class="list mt-2">
                    <p class="text-12 text-weight-bold mb-1 text-center" v-if="filteredFloorplans.filterTrue && filteredFloorplans.filterTrue.length == 0">
                        {{ $t('Components_Issues_IssueList_21') }}
                    </p>
                    <!-- FLOORPLANS FILTER TRUE -->
                    <li
                        @click="floorplanClick(floorplan)"
                        @dblclick="floorplanDoubleClick(floorplan)"
                        v-for="(floorplan, index) in filteredFloorplans.filterTrue"
                        :key="index"
                        class="list-item d-flex flex-row align-items-center"
                        :class="{
                            'active-item': floorplan.id == selectedFloorplan.id,
                        }"
                    >
                        <icon class="mr-2" name="ci-layer" scale=".75" />
                        <p class="text-12">{{ floorplan.name }}</p>
                    </li>
                    <div class="list-divider"></div>
                    <!-- FLOORPLANS FILTER FALSE -->
                    <li
                        @click="floorplanClick(floorplan)"
                        @dblclick="floorplanDoubleClick(floorplan)"
                        v-for="(floorplan, index) in filteredFloorplans.filterFalse"
                        :key="'filter-false-' + index"
                        class="list-item filter-false-item d-flex flex-row align-items-center"
                        :class="{
                            'active-item': floorplan.id == selectedFloorplan.id,
                        }"
                    >
                        <icon class="mr-2" name="ci-layer" scale=".75" />
                        <p class="text-12">{{ floorplan.name }}</p>
                    </li>
                </ul>
            </vue-custom-scrollbar>
        </div>
    </aside>
</template>

<script>
// ====================
// Icons
// ====================
import 'vue-awesome/icons/layer-group'
import VueCustomScrollbar from 'vue-custom-scrollbar'

// Vuex
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    props: {
        /**
         * determines if this component is open
         */
        isOpen: {
            type: Boolean,
            default: false,
        },
        /**
         * the entire project
         */
        project: {
            type: Object,
        },
        /**
         * determines if subsidebar headers are showing or not (when mouse is hovering)
         */
        isShowingSubSidebarHeaders: {
            type: Boolean,
            default: false,
        },
        /**
         * sector that user selected (navigation purposes)
         */
        selectedSector: {
            default: -1,
        },
        /**
         * floorplan that user selected (navigation purposes)
         */
        selectedFloorplan: {
            default: -1,
        },
        /**
         * current sector of the showing viewpoint
         */
        currentSector: {
            default: -1,
        },
        /**
         * current floorplan of the showing viewpoint
         */
        currentFloorplan: {
            default: -1,
        },
    },
    components: {
        VueCustomScrollbar,
    },
    data() {
        return {
            searchText: '',
            filteredFloorplans: {
                filterTrue: [],
                filterFalse: [],
            },
            isDatesFilterOpen: false,
            selectedDatesArray: [],
        }
    },
    computed: {
        ...mapState(['dateFilterViewer']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
    },
    watch: {
        dateFilterViewer: {
            handler() {
                this.selectedDatesArray = this.getDatesByProjectId(this.$route.params.id)
            },
            deep: true,
        },
        searchText() {
            // Floorplans
            this.filterFloorplans()
        },
        selectedSector() {
            this.filteredFloorplans.filterTrue = this.selectedSector.floorplans
            this.resetFilters()
            this.$emit('changeSelectedSector', this.selectedSector)
        },
        isOpen() {
            if (!this.isOpen) {
                this.isDatesFilterOpen = false
            } else {
                this.filterFloorplans()
            }
        },
        selectedDatesArray() {
            this.setDates({ projectId: this.$route.params.id, dates: this.selectedDatesArray })
            // Floorplans
            if (this.isOpen) this.filterFloorplans()
        },
    },
    methods: {
        ...mapActions('dateFilterViewer', ['setDates']),
        scrollToTop() {
            this.$refs.scroll.$el.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            })
        },
        showSubSidebarHeaders() {
            if (this.isOpen) this.$emit('showSubSidebarHeaders')
        },
        floorplanClick(floorplan) {
            this.$emit('floorplanClick', floorplan)
            this.isDatesFilterOpen = false
        },
        floorplanDoubleClick(floorplan) {
            this.$emit('floorplanDblClick', floorplan)
            this.isDatesFilterOpen = false
        },
        // Filtros
        filterFloorplans() {
            this.filteredFloorplans.filterTrue = []
            this.filteredFloorplans.filterFalse = []
            this.selectedSector.floorplans.forEach((floorplan) => {
                if (floorplan.name.toLowerCase().includes(this.searchText.toLowerCase()) && this.checkFloorplanDate(floorplan)) {
                    this.filteredFloorplans.filterTrue.push(floorplan)
                } else {
                    this.filteredFloorplans.filterFalse.push(floorplan)
                }
            })
            this.scrollToTop()
        },
        checkFloorplanDate(floorplan) {
            if (this.selectedDatesArray && this.selectedDatesArray.length == 0) return true
            let flag = false
            floorplan.viewpoints.forEach((vp) => {
                vp.images.forEach((img) => {
                    let imgDate = img.date.split(' ')[0]
                    this.selectedDatesArray.forEach((date) => {
                        if (date == imgDate) {
                            flag = true
                        }
                    })
                })
            })

            return flag
        },
        resetFilters() {
            this.searchText = ''
        },
    },
    mounted() {
        this.filteredFloorplans.filterTrue = this.selectedSector.floorplans
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/generic-sub-sidebar.scss';
@import '@/scss/components/360Viewer/Sidebar/Floorplans/sectors-sidebar.scss';
</style>
